import { NgModule, APP_INITIALIZER, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NotFoundPageComponent } from '@app/core/containers/not-found-page';
import { ReleaseNotesPageComponent } from '@app/core/containers/release-notes';
import { AuthGuard } from '@app/users/guards/auth.guard';
import { CoreValsGuard, UrlPermissionsGuard } from '@app/core/guards';
import { RedirectService } from '@app/shared/services/redirect.service';
import { LocationStrategy, HashLocationStrategy } from '@angular/common';
import { Subscription } from 'rxjs';

export function initializeUserIndex(redirectService: RedirectService, router: Router): () => Promise<void> {
  return async () => {
    let currentUrl = window.location.href;
    
    let userIndex = '0';
    const hashMatch = currentUrl.match(/#\/(\d+)/);
    if (hashMatch) {
      userIndex = hashMatch[1];
    }
    
    console.log('APP_INITIALIZER userIndex:', userIndex);
    updateRoutes(router, userIndex);
  };
}

function updateRoutes(router: Router, userIndex: string) {
  router.resetConfig([
    {
      path: `${userIndex}/login`,
      loadChildren: () => import('@app/users/users.module').then(m => m.UsersModule),
    },
    {
      path: `${userIndex}/crm`,
      loadChildren: () => import('@app/crm/crm.module').then(m => m.CRMModule),
      canActivate: [AuthGuard, CoreValsGuard],
    },
    {
      path: `${userIndex}/trips`,
      loadChildren: () => import('@app/trips/trips.module').then(m => m.TripsModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/quotes`,
      loadChildren: () => import('@app/quotes/quotes.module').then(m => m.QuotesModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/loadboard`,
      loadChildren: () => import('@app/quotes/quotes.module').then(m => m.QuotesModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/calendar`,
      loadChildren: () => import('@app/calendar/calendar.module').then(m => m.CalendarModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/tracking`,
      loadChildren: () => import('@app/tracking/tracking.module').then(m => m.TrackingModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/tasks`,
      loadChildren: () => import('@app/tasks/tasks.module').then(m => m.TasksModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/claims`,
      loadChildren: () => import('@app/claims/claims.module').then(m => m.ClaimsModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/reports`,
      loadChildren: () => import('@app/reports/reports.module').then(m => m.ReportsModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/settlements`,
      loadChildren: () => import('@app/settlements/settlements.module').then(m => m.SettlementsModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/admin`,
      loadChildren: () => import('@app/admin/admin.module').then(m => m.AdminModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/recruiter`,
      loadChildren: () => import('@app/recruiter/recruiter.module').then(m => m.RecruiterModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/schedules`,
      loadChildren: () => import('@app/schedule/schedule.module').then(m => m.ScheduleModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/v`,
      loadChildren: () => import('@app/public/public.module').then(m => m.PublicModule),
      canActivate: [AuthGuard, CoreValsGuard],
    },
    {
      path: `${userIndex}/shop`,
      loadChildren: () => import('@app/shop/shop.module').then(m => m.ShopModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/expenses`,
      loadChildren: () => import('@app/expense/expenses.module').then(m => m.ExpensesModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/dashboards`,
      loadChildren: () => import('@app/dashboards/dashboards.module').then(m => m.DashboardsModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/feedback`,
      loadChildren: () => import('@app/feedbacks/feedbacks.module').then(m => m.FeedbacksModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/workflow`,
      loadChildren: () => import('@app/workflow/workflows.module').then(m => m.WorkflowsModule),
      canActivate: [AuthGuard, CoreValsGuard, UrlPermissionsGuard],
    },
    {
      path: `${userIndex}/integrations`,
      loadChildren: () => import('@app/integrations/integrations.module').then(m => m.IntegrationsModule),
      canActivate: [AuthGuard, CoreValsGuard],
    },
    { path: `${userIndex}/release-notes`, component: ReleaseNotesPageComponent, canActivate: [AuthGuard, CoreValsGuard] },
    { path: `${userIndex}`, redirectTo: `${userIndex}/quotes`, pathMatch: 'full' },
    { path: ``, redirectTo: `${userIndex}`, pathMatch: 'full' },
    { path: `**`, component: NotFoundPageComponent },
  ]);
}

@NgModule({
  imports: [
    RouterModule.forRoot([], {
      useHash: true,
      paramsInheritanceStrategy: 'emptyOnly',
    }),
  ],
  providers: [
    AuthGuard,
    CoreValsGuard,
    UrlPermissionsGuard,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeUserIndex,
      deps: [RedirectService, Router],
      multi: true,
    },
    RedirectService,
    { provide: LocationStrategy, useClass: HashLocationStrategy },
  ],
  exports: [RouterModule],
})
export class AppRoutingModule implements OnDestroy {
  private subscription: Subscription;

  constructor(private router: Router, private redirectService: RedirectService) {
    this.subscription = this.redirectService.userIndex$.subscribe((user) => {
      let userLocal: any = user
      let userIndex = userLocal?.userIndex || 0
      if (userLocal?.userIndex || userLocal?.userIndex == 0) {
        updateRoutes(router, String(userIndex))
        const currentHash = window.location.hash
        if (currentHash.includes('/login')) {
          this.router.navigate([`/${userIndex}${userLocal?.isLoadboard ? '/loadboard' : '/quotes'}`])
        }
      }

    })
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
